import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type Ingesta as IngestaRow, type Sede } from '../lib/api.js';
import { UploadDropzone } from '../components/ingesta/UploadDropzone.js';
import { ReviewPanel } from '../components/ingesta/ReviewPanel.js';
import { ResStatReviewPanel } from '../components/ingesta/ResStatReviewPanel.js';
import { SourceReviewPanel } from '../components/ingesta/SourceReviewPanel.js';
import { RoomTypeReviewPanel } from '../components/ingesta/RoomTypeReviewPanel.js';
import { DiamondForm } from '../components/ingesta/DiamondForm.js';
import {
  fechaLabel,
  groupByFecha,
  LIBROS,
  resumenLibro,
  SIN_FECHA,
  TIPO_BADGE,
  TIPOS_LIBRO,
} from '../lib/ingesta-cola.js';

/**
 * Los reportes que se pueden subir. `cuentas` es un reporte más para el
 * usuario, pero por dentro no toma el mismo camino: los cuatro primeros son
 * PDFs que van a R2 y los lee Gemini en cola, y el libro Excel se importa al
 * momento (`/ingesta/excel`) sin pasar por la revisión.
 *
 * Los dos caminos sí acaban en el MISMO sitio: todo lo que entra queda listado
 * abajo con su archivo, de modo que la lista sea de verdad «lo ingerido» y no
 * «lo que pasó por Gemini».
 */
type Tipo = 'flash' | 'diamond' | 'source' | 'roomtype' | 'cuentas';

/** Los que sí van a la cola de extracción (lo que acepta `POST /ingesta/upload`). */
type TipoCola = Exclude<Tipo, 'cuentas'>;

const TIPOS: Tipo[] = ['flash', 'diamond', 'source', 'roomtype', 'cuentas'];

/** Nombre corto de cada reporte, para los mensajes y el selector. */
const TIPO_NOMBRE: Record<Tipo, string> = {
  flash: 'Manager-Flash',
  diamond: 'Reservation Statistics 2',
  source: 'Res. Statistics por Source',
  roomtype: 'Statistics por tipo de habitación',
  cuentas: 'Libros mensuales (Excel)',
};

const SUB_COLA =
  'Se suben a R2 y se extraen con Gemini automáticamente (en cola); luego quedan en la cola de revisión para confirmar.';

/** Qué dice el dropzone según el tipo elegido. */
const TIPO_HINT: Record<Tipo, string> = {
  flash: 'Arrastra aquí los PDFs del Manager-Flash o haz clic',
  diamond: 'Arrastra aquí el PDF del Reservation Statistics 2 (de él sale Diamond) o haz clic',
  source:
    'Arrastra aquí el PDF del Reservation Statistics por Source (mensual: de él salen los canales CDR/VIP/VTA/Front Desk) o haz clic',
  roomtype:
    'Arrastra aquí los PDFs de «Statistics - Room Type» (uno por tipo de habitación; puedes soltarlos todos juntos) o haz clic',
  cuentas: 'Arrastra aquí el libro «RESULTADOS ACCOUNTS» o el de Expedia (.xlsx) o haz clic',
};

const TIPO_SUB: Record<Tipo, string> = {
  flash: SUB_COLA,
  diamond: SUB_COLA,
  source: SUB_COLA,
  roomtype: SUB_COLA,
  cuentas:
    'Los dos libros mensuales: el de la Central de Reservas (un bloque por cuenta) y el de Expedia (una hoja por sede, una fila por reserva). El Worker distingue cuál es por su FORMA y lo enruta solo. Se importa al momento — no pasa por la cola de revisión.',
};

/**
 * Los dos libros Excel que se suben. El de Noches Diamond/Comp/House/Total lo
 * GENERA el sistema desde la carga diaria y se descarga en Reportes; si alguien
 * lo suelta aquí, el Worker lo reconoce por su forma y lo rechaza con su motivo.
 */
const LIBRO_NOMBRE = 'cuentas corporativas (RESULTADOS ACCOUNTS, un bloque por cuenta)';

/** El otro libro que entra por aquí: Expedia, a nivel de reserva. */
const LIBRO_EXPEDIA = 'Expedia (una hoja por sede, una fila por reserva)';

/** Dónde se ve lo que entró: alimenta el Dashboard y queda listado abajo. */
const LIBRO_DESTINO =
  ' Alimenta los paneles del Dashboard y queda registrado abajo, en «Libros mensuales (Excel)».';

const ESTADO_TONE: Record<string, string> = {
  recibido: 'bg-slate-700 text-slate-200',
  extraido: 'bg-blue-900 text-blue-200',
  validado: 'bg-indigo-900 text-indigo-200',
  committed: 'bg-emerald-900 text-emerald-200',
  error: 'bg-rose-900 text-rose-200',
};

export function Ingesta() {
  const [sedes, setSedes] = useState<Sede[]>([]);
  const [rows, setRows] = useState<IngestaRow[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [dupFiles, setDupFiles] = useState<File[]>([]);
  const [tipo, setTipo] = useState<Tipo>('flash');
  // Track which groups are EXPANDED — empty = all collapsed (default). New groups
  // (e.g. after a refresh/upload) start collapsed; the user clicks to expand.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const groups = useMemo(() => groupByFecha(rows), [rows]);
  // Derive from the CURRENT groups so stale keys never desync the toggle label.
  const allExpanded = groups.length > 0 && groups.every((g) => expanded.has(g.key));

  // Items still in 'recibido' are queued/extracting in the background → poll so
  // the queue shows live progress without manual refresh.
  const pendientesCount = rows.filter((r) => r.estado === 'recibido').length;
  const erroresCount = rows.filter((r) => r.estado === 'error').length;

  async function processPending() {
    try {
      await api.ingestaProcessPending();
    } catch {
      /* ignore — refresh will reflect any change */
    }
    refresh();
  }

  async function retryFailed() {
    try {
      await api.ingestaRetryFailed();
    } catch {
      /* ignore — refresh will reflect any change */
    }
    refresh();
  }

  /**
   * Recupera el archivo tal como se subió. Para un libro Excel es lo único que
   * se puede hacer con él después del import: no hay panel de revisión porque
   * no hay nada que confirmar.
   */
  async function descargar(r: IngestaRow) {
    try {
      const url = await api.ingestaFileUrl(r.id);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.filename ?? `${r.tipo}-${r.id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'No se pudo descargar el archivo');
    }
  }

  async function del(id: string) {
    const esLibro = TIPOS_LIBRO.has(rows.find((r) => r.id === id)?.tipo ?? '');
    if (
      !window.confirm(
        esLibro
          ? '¿Quitar este libro de la lista? Se borra el archivo subido y su registro. Lo que el libro YA importó (cuentas / Expedia) NO se borra: para deshacerlo hay que volver a subir el mes corregido.'
          : '¿Eliminar este reporte? Se borra el archivo subido y su fila de la cola; los datos ya confirmados (registro del día) se conservan.',
      )
    )
      return;
    try {
      await api.ingestaDelete(id);
      if (selected === id) setSelected(null);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Error al eliminar');
    }
    refresh();
  }

  async function delFailed() {
    if (!window.confirm(`¿Eliminar ${erroresCount} reporte(s) con error?`)) return;
    try {
      const r = await api.ingestaDeleteFailed();
      setMsg(`Eliminados ${r.deleted} reporte(s) con error.`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Error al eliminar');
    }
    refresh();
  }

  /**
   * Import del libro de cuentas corporativas. Sin `libro` explícito, el Worker
   * mira la FORMA del archivo: si le llega el de noches lo rechaza diciendo que
   * ése ahora lo genera el sistema, en vez de escribir filas basura.
   *
   * `desviado` = el usuario tenía seleccionado un tipo de PDF pero soltó un
   * Excel. Se importa igual (es el único libro que entra por aquí), pero se
   * avisa, porque ese atajo ignora el selector de arriba.
   */
  async function importExcel(file: File, opts: { desviado?: boolean } = {}) {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.ingestaImportExcel(file);
      const yrs = r.years.length ? ` (años ${r.years.join(', ')})` : '';
      const cuentas = r.cuentas != null ? ` · ${r.cuentas} cuenta(s)` : '';
      const esExpedia = r.libro === 'expedia';
      const nombre = esExpedia ? LIBRO_EXPEDIA : LIBRO_NOMBRE;
      const expedia = esExpedia
        ? ` · ${r.noches ?? 0} noche(s) de ${r.reservas ?? 0} reserva(s)` +
          (r.canceladas ? ` y ${r.canceladas} cancelada(s)` : '')
        : '';
      const iss = r.issues.length
        ? ` · ${r.issues.length} aviso(s): ${r.issues.slice(0, 3).join(' | ')}${r.issues.length > 3 ? '…' : ''}`
        : '';
      const nota = opts.desviado
        ? ` Ojo: un .xlsx SIEMPRE va al libro de cuentas, no al reporte ${TIPO_NOMBRE[tipo]}, que se sube en PDF. Para subirlo sin este aviso, elige arriba «${TIPO_NOMBRE.cuentas}».`
        : '';
      setMsg(
        r.imported === 0
          ? `No se importó ninguna fila de «${file.name}»: el libro no tiene el formato esperado de ${nombre}.${iss}${nota}`
          : `Importadas ${r.imported} fila(s) del libro de ${nombre}${cuentas}${expedia}${yrs}${iss}${r.imported > 0 ? LIBRO_DESTINO : ''}${nota}`,
      );
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Error al importar el Excel');
    } finally {
      setBusy(false);
    }
  }

  function toggleGroup(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // Guard against setState after unmount (in-flight polling/refresh requests).
  const alive = useRef(true);
  useEffect(() => () => void (alive.current = false), []);

  const refresh = useCallback(() => {
    api
      .ingestaList()
      .then((r) => alive.current && setRows(r.ingestas))
      .catch(() => alive.current && setRows([]));
  }, []);

  useEffect(() => {
    api
      .sedes()
      .then((r) => alive.current && setSedes(r.sedes))
      .catch(() => alive.current && setSedes([]));
    refresh();
  }, [refresh]);

  // Poll while the background extraction queue still has 'recibido' items.
  // Capped so a genuinely stuck item (e.g. GEMINI_API_KEY unset) can't poll
  // forever: when progress is made `pendientesCount` changes and this effect
  // re-runs with a fresh counter; with no progress it stops after ~2 min.
  useEffect(() => {
    if (pendientesCount === 0) return;
    let polls = 0;
    const t = setInterval(() => {
      refresh();
      if (++polls >= 40) clearInterval(t);
    }, 3000);
    return () => clearInterval(t);
  }, [pendientesCount, refresh]);

  async function doUpload(files: File[], replace: boolean) {
    // El libro de cuentas no pasa por la cola: `onFiles` lo enruta a
    // `importExcel` antes de llegar aquí. El guard estrecha el tipo.
    if (tipo === 'cuentas') return;
    const tipoCola: TipoCola = tipo;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.ingestaUpload(files, replace, tipoCola);
      const stored = r.uploaded.filter((u) => u.stored).length;
      const dups = r.uploaded.filter((u) => u.duplicate && !u.stored);
      const tail =
        r.queued > 0
          ? ` Extrayendo ${r.queued} automáticamente…`
          : stored > 0
            ? ' La extracción automática no está disponible ahora; revísalos manualmente.'
            : '';
      if (dups.length > 0 && !replace) {
        const dupNames = new Set(dups.map((d) => d.file));
        setDupFiles(files.filter((f) => dupNames.has(f.name)));
        setMsg(
          `Subido(s) ${stored} archivo(s). ${dups.length} ya existe(n) con contenido idéntico.${tail}`,
        );
      } else {
        setDupFiles([]);
        setMsg(`Subido(s) ${stored} archivo(s)${replace ? ' (reemplazo forzado)' : ''}.${tail}`);
      }
      refresh();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Error al subir');
    } finally {
      setBusy(false);
    }
  }

  function onFiles(files: File[]) {
    setDupFiles([]);
    if (files.length === 0) return;
    const excel = files.find(
      (f) =>
        /\.xlsx?$/i.test(f.name) || f.type.includes('spreadsheetml') || f.type.includes('ms-excel'),
    );

    // Tipo «Cuentas corporativas»: solo entra el libro, y un PDF suelto aquí se
    // rechaza con su motivo en vez de irse por el camino equivocado.
    if (tipo === 'cuentas') {
      if (!excel) {
        setMsg(
          `No se importó nada: ${files.map((f) => f.name).join(', ')} no es un .xlsx. El libro de ${LIBRO_NOMBRE} es un Excel; los reportes en PDF se suben eligiendo su tipo arriba.`,
        );
        return;
      }
      void importExcel(excel);
      return;
    }

    // Un .xlsx soltado con un tipo de PDF seleccionado va igualmente al import
    // de libros, no a Gemini (que falla al no ser un PDF). El Worker decide por
    // la FORMA: si es el de noches, responde que ése ya lo genera el sistema.
    // Se avisa, porque ese atajo IGNORA el selector de tipo de arriba.
    if (excel) {
      void importExcel(excel, { desviado: true });
      return;
    }
    // Los dos reportes se leen con Gemini: solo PDF o imagen. Cualquier otra
    // cosa se rechaza aquí con un motivo visible en vez de subirse para fallar
    // después en la cola.
    const legibles = files.filter(
      (f) => /\.(pdf|png|jpe?g|webp|heic)$/i.test(f.name) || f.type.startsWith('image/'),
    );
    if (legibles.length === 0) {
      setMsg(
        `No se subió nada: ${files.map((f) => f.name).join(', ')} no es PDF ni imagen. El reporte ${TIPO_NOMBRE[tipo]} debe subirse en PDF.`,
      );
      return;
    }
    if (legibles.length < files.length) {
      setMsg(`Se omitieron ${files.length - legibles.length} archivo(s) que no son PDF ni imagen.`);
    }
    void doUpload(legibles, false);
  }

  return (
    <div className="space-y-5">
      <h1 className="text-lg font-semibold text-slate-100">Ingesta de reportes</h1>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-400">Tipo de reporte:</span>
        <div className="inline-flex overflow-hidden rounded-md border border-slate-700">
          {TIPOS.map((t) => (
            <button
              key={t}
              onClick={() => setTipo(t)}
              className={`px-3 py-1 ${tipo === t ? 'bg-blue-600 text-white' : 'bg-slate-900 text-slate-300'}`}
            >
              {TIPO_NOMBRE[t]}
            </button>
          ))}
        </div>
      </div>

      <UploadDropzone
        onFiles={onFiles}
        busy={busy}
        hint={TIPO_HINT[tipo]}
        sub={TIPO_SUB[tipo]}
        accept={
          tipo === 'cuentas'
            ? '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : undefined
        }
        multiple={tipo !== 'cuentas'}
      />

      {msg && <p className="text-sm text-slate-400">{msg}</p>}

      {dupFiles.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-700/60 bg-amber-950/30 p-3 text-sm text-amber-200">
          <span>
            {dupFiles.length} archivo(s) con contenido idéntico a uno ya cargado. ¿Subir igual?
          </span>
          <button
            onClick={() => void doUpload(dupFiles, true)}
            disabled={busy}
            className="rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-500 disabled:opacity-50"
          >
            Subir de todos modos
          </button>
          <button onClick={() => setDupFiles([])} className="text-xs text-amber-300/80 underline">
            Descartar
          </button>
        </div>
      )}

      {selected &&
        (rows.find((r) => r.id === selected)?.tipo === 'roomtype' ? (
          <RoomTypeReviewPanel
            id={selected}
            onClose={() => setSelected(null)}
            onCommitted={() => {
              setSelected(null);
              refresh();
            }}
          />
        ) : rows.find((r) => r.id === selected)?.tipo === 'source' ? (
          <SourceReviewPanel
            id={selected}
            onClose={() => setSelected(null)}
            onCommitted={() => {
              setSelected(null);
              refresh();
            }}
          />
        ) : rows.find((r) => r.id === selected)?.tipo === 'diamond' ? (
          <ResStatReviewPanel
            id={selected}
            onClose={() => setSelected(null)}
            onCommitted={() => {
              setSelected(null);
              refresh();
            }}
          />
        ) : (
          <ReviewPanel
            id={selected}
            onClose={() => setSelected(null)}
            onCommitted={() => {
              setSelected(null);
              refresh();
            }}
          />
        ))}

      <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-medium text-slate-200">
            Cola de ingesta ({rows.length}) · {groups.length} día(s)
            {pendientesCount > 0 && (
              <span className="rounded-full bg-blue-900 px-2 py-0.5 text-xs text-blue-200">
                ⏳ extrayendo {pendientesCount}…
              </span>
            )}
          </h2>
          <div className="flex items-center gap-3">
            {erroresCount > 0 && (
              <button
                onClick={retryFailed}
                title="Reintenta la extracción de los reportes que fallaron"
                className="text-xs text-rose-300 underline"
              >
                Reintentar fallidos ({erroresCount})
              </button>
            )}
            {erroresCount > 0 && (
              <button
                onClick={delFailed}
                title="Elimina todos los reportes con error"
                className="text-xs text-rose-400 underline"
              >
                Eliminar fallidos ({erroresCount})
              </button>
            )}
            {pendientesCount > 0 && (
              <button
                onClick={processPending}
                title="Re-encola los reportes que quedaron en 'recibido' sin extraer"
                className="text-xs text-slate-400 underline"
              >
                Procesar pendientes
              </button>
            )}
            {groups.length > 0 && (
              <button
                onClick={() =>
                  setExpanded(allExpanded ? new Set() : new Set(groups.map((g) => g.key)))
                }
                className="text-xs text-slate-400 underline"
              >
                {allExpanded ? 'Colapsar todo' : 'Expandir todo'}
              </button>
            )}
            <button onClick={refresh} className="text-xs text-slate-400 underline">
              Refrescar
            </button>
          </div>
        </div>

        {groups.length === 0 && (
          <p className="px-2 py-4 text-center text-slate-500">Sin ingestas todavía.</p>
        )}

        <div className="space-y-2">
          {groups.map((g) => {
            const isOpen = expanded.has(g.key);
            const errores = g.rows.filter((r) => r.estado === 'error').length;
            const pendientes = g.rows.filter((r) => r.estado !== 'committed').length;
            return (
              <div key={g.key} className="overflow-hidden rounded-lg border border-slate-700">
                <button
                  onClick={() => toggleGroup(g.key)}
                  className="flex w-full items-center justify-between gap-2 bg-slate-900/60 px-3 py-2 text-left hover:bg-slate-900"
                >
                  <span className="flex items-center gap-2 text-sm text-slate-200">
                    <span className="text-slate-500">{isOpen ? '▾' : '▸'}</span>
                    {g.key === SIN_FECHA ? (
                      <span className="text-amber-300">Sin fecha · pendiente de extracción</span>
                    ) : g.key === LIBROS ? (
                      <span className="font-medium">
                        Libros mensuales (Excel)
                        <span className="ml-2 text-xs font-normal text-slate-500">
                          importados al momento, sin cola de revisión
                        </span>
                      </span>
                    ) : (
                      <span className="font-medium">{fechaLabel(g.key)}</span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 text-xs text-slate-400">
                    {errores > 0 && (
                      <span className="rounded bg-rose-900 px-1.5 py-0.5 text-rose-200">
                        {errores} error
                      </span>
                    )}
                    {pendientes > 0 && g.key !== SIN_FECHA && g.key !== LIBROS && (
                      <span className="rounded bg-slate-700 px-1.5 py-0.5 text-slate-200">
                        {pendientes} sin commit
                      </span>
                    )}
                    <span>{g.rows.length} reporte(s)</span>
                  </span>
                </button>

                {isOpen && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase text-slate-500">
                          <th className="px-3 py-1">Estado</th>
                          <th className="px-3 py-1">Reporte</th>
                          <th className="px-3 py-1">Sede</th>
                          <th className="px-3 py-1">Archivo</th>
                          <th className="px-3 py-1">Subido</th>
                          <th className="px-3 py-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.rows.map((r) => {
                          const esLibro = TIPOS_LIBRO.has(r.tipo);
                          const badge = TIPO_BADGE[r.tipo];
                          const resumen = resumenLibro(r.resumen);
                          return (
                            <tr key={r.id} className="border-t border-slate-800 align-top">
                              <td className="px-3 py-1.5">
                                <span
                                  className={`rounded px-2 py-0.5 text-xs ${ESTADO_TONE[r.estado] ?? 'bg-slate-700'}`}
                                >
                                  {r.estado}
                                </span>
                              </td>
                              <td className="px-3 py-1.5">
                                <span
                                  className={`rounded px-1.5 py-0.5 text-[10px] ${badge?.tone ?? 'bg-slate-700 text-slate-200'}`}
                                >
                                  {badge?.label ?? r.tipo}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-slate-300">
                                {/* Un libro trae VARIAS sedes en el mismo archivo. */}
                                {esLibro
                                  ? (resumen ?? '—')
                                  : (sedes.find((s) => s.id === r.sede_id)?.codigo ?? '—')}
                                {r.error_msg && (
                                  <span className="block text-[11px] text-rose-300">
                                    {r.error_msg}
                                  </span>
                                )}
                              </td>
                              <td
                                className="max-w-[16rem] truncate px-3 py-1.5 text-slate-400"
                                title={r.filename ?? ''}
                              >
                                {r.filename ?? '—'}
                              </td>
                              <td className="px-3 py-1.5 text-slate-500">
                                {r.uploaded_at?.slice(0, 16).replace('T', ' ')}
                              </td>
                              <td className="whitespace-nowrap px-3 py-1.5 text-right">
                                {/* Un libro no tiene nada que revisar: se importó al
                                    momento. Lo que sí sirve es recuperar el archivo. */}
                                {esLibro ? (
                                  <button
                                    onClick={() => descargar(r)}
                                    className="text-xs text-blue-400 hover:underline"
                                  >
                                    Descargar
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setSelected(r.id)}
                                    className="text-xs text-blue-400 hover:underline"
                                  >
                                    Revisar →
                                  </button>
                                )}
                                <button
                                  onClick={() => del(r.id)}
                                  title={
                                    esLibro
                                      ? 'Quita el archivo de esta lista. Lo que el libro ya importó NO se borra.'
                                      : 'Eliminar este reporte'
                                  }
                                  className="ml-3 text-xs text-rose-400 hover:underline"
                                >
                                  Eliminar
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <DiamondForm sedes={sedes} />
    </div>
  );
}
